/**
 * FX rate utility — fetches historical exchange rates from api.frankfurter.dev
 * (free, no API key, ECB daily rates, supports historical time series).
 *
 * Rates are cached in localStorage under STORAGE_KEYS.fxRates keyed by
 * "{from}-{to}" with monthly granularity (YYYY-MM → rate at last available
 * business day of that month). A 'latest' key holds the most recent snapshot.
 *
 *   cache = {
 *     "USD-ILS": { "2024-01": 3.64, "2024-02": 3.67, latest: 3.69, latestFetchedAt: "..." },
 *     ...
 *   }
 *
 * Rate semantics: `rate = to-units per 1 from-unit`. So if from=USD, to=ILS,
 * and rate=3.65, then $1 = ₪3.65.
 */

import { STORAGE_KEYS } from './constants';

const API_BASE = 'https://api.frankfurter.dev/v1';

// In-memory cache. Hydrated from localStorage on first access; every write
// persists back. Module-level so all callers share one snapshot.
let memCache = null;
function load() {
  if (memCache) return memCache;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.fxRates);
    memCache = raw ? JSON.parse(raw) : {};
  } catch {
    memCache = {};
  }
  return memCache;
}
function persist() {
  try { localStorage.setItem(STORAGE_KEYS.fxRates, JSON.stringify(memCache || {})); } catch {}
}
function pairKey(from, to) { return `${from}-${to}`; }

/** Read-only cache accessor — returns a snapshot for the given pair (or {}). */
export function getCachedPair(from, to) {
  const c = load();
  return c[pairKey(from, to)] || {};
}

/** Replace the in-memory cache (used for tests). Does not persist. Pass null to force re-hydration from localStorage on next read. */
export function __setCacheForTest(next) { memCache = next === null ? null : (next || {}); }

/** Month helpers */
function ymOf(dateStr) { return String(dateStr).slice(0, 7); }
function monthEndDate(year, monthIdx) {
  // last day of the month as a 'YYYY-MM-DD' string
  return new Date(Date.UTC(year, monthIdx + 1, 0)).toISOString().slice(0, 10);
}
function monthsBetween(startYM, endYM) {
  const [sy, sm] = startYM.split('-').map(Number);
  const [ey, em] = endYM.split('-').map(Number);
  const out = [];
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return out;
}

/**
 * Look up the rate for a specific month, with fallback chain.
 * Priority: explicit override (caller-supplied) → cache hit on month →
 *           nearest earlier cached month → nearest later cached month → null.
 *
 * Pure — never hits the network.
 */
export function getRateForMonth(from, to, ym, { override } = {}) {
  if (override != null && Number.isFinite(Number(override))) return Number(override);
  if (!from || !to) return null;
  if (from === to) return 1;
  const pair = getCachedPair(from, to);
  if (pair[ym] != null) return pair[ym];

  const keys = Object.keys(pair).filter(k => /^\d{4}-\d{2}$/.test(k)).sort();
  if (keys.length === 0) {
    if (pair.latest != null) return pair.latest;
    return null;
  }
  // nearest earlier
  let earlier = null;
  for (const k of keys) { if (k <= ym) earlier = k; else break; }
  if (earlier) return pair[earlier];
  // else nearest later
  return pair[keys[0]];
}

/** Most recent known rate, or null. */
export function getLatestRate(from, to) {
  if (from === to) return 1;
  const pair = getCachedPair(from, to);
  if (pair.latest != null) return pair.latest;
  const keys = Object.keys(pair).filter(k => /^\d{4}-\d{2}$/.test(k)).sort();
  if (keys.length === 0) return null;
  return pair[keys[keys.length - 1]];
}

/**
 * Fetch a time series of daily rates and collapse to monthly points by taking
 * the last available business day in each month.
 *
 * Frankfurter response shape:
 *   { amount: 1, base: 'USD', start_date: '...', end_date: '...',
 *     rates: { 'YYYY-MM-DD': { ILS: 3.65 }, ... } }
 *
 * Returns { 'YYYY-MM': rate } for all months covered by the response.
 * Throws on network failure.
 */
export async function fetchTimeSeries(from, to, startYM, endYM, fetchImpl = fetch) {
  if (from === to) return {};
  const start = `${startYM}-01`;
  const [ey, em] = endYM.split('-').map(Number);
  const end = monthEndDate(ey, em - 1);
  const url = `${API_BASE}/${start}..${end}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  const res = await fetchImpl(url);
  if (!res.ok) throw new Error(`Frankfurter ${res.status}`);
  const body = await res.json();
  const rates = body.rates || {};
  const byMonth = {}; // { 'YYYY-MM': { date, rate } }
  Object.entries(rates).forEach(([date, obj]) => {
    const rate = obj && obj[to];
    if (rate == null) return;
    const ym = ymOf(date);
    if (!byMonth[ym] || date > byMonth[ym].date) byMonth[ym] = { date, rate };
  });
  const out = {};
  Object.entries(byMonth).forEach(([ym, { rate }]) => { out[ym] = rate; });
  return out;
}

/** Fetch the single most recent rate. */
export async function fetchLatest(from, to, fetchImpl = fetch) {
  if (from === to) return 1;
  const url = `${API_BASE}/latest?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  const res = await fetchImpl(url);
  if (!res.ok) throw new Error(`Frankfurter ${res.status}`);
  const body = await res.json();
  const r = body.rates && body.rates[to];
  if (r == null) throw new Error('Rate missing in response');
  return r;
}

/**
 * Ensure we have rates for every month in [startYM..endYM] + latest.
 * Batches one `fetchTimeSeries` call for the whole range and persists.
 * Silently no-ops on network failure (caller falls back to whatever's cached).
 */
export async function ensureRatesForRange(from, to, startYM, endYM, fetchImpl = fetch) {
  if (from === to) return;
  const cache = load();
  const key = pairKey(from, to);
  const pair = cache[key] || {};

  const needed = monthsBetween(startYM, endYM).filter(ym => pair[ym] == null);
  const needLatest = pair.latest == null
    || !pair.latestFetchedAt
    || (Date.now() - new Date(pair.latestFetchedAt).getTime()) > 24 * 60 * 60 * 1000;

  if (needed.length === 0 && !needLatest) return;

  try {
    if (needed.length > 0) {
      const sorted = needed.slice().sort();
      const fetched = await fetchTimeSeries(from, to, sorted[0], sorted[sorted.length - 1], fetchImpl);
      Object.assign(pair, fetched);
    }
    if (needLatest) {
      try {
        const latest = await fetchLatest(from, to, fetchImpl);
        pair.latest = latest;
        pair.latestFetchedAt = new Date().toISOString();
      } catch {
        // latest fetch is best-effort
      }
    }
    cache[key] = pair;
    memCache = cache;
    persist();
  } catch {
    // Network failure — callers degrade gracefully via cache fallback.
  }
}

/** Convenience: same-currency short-circuit for callers. */
export function isSameCurrency(from, to) {
  return !from || !to || from === to;
}
